import { SingleSelectWithOther } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { usePostShippingInfoMutation } from 'store/api/onboardingApi';
import { usePostOnboardingActivityMutation } from 'store/api/userSupportApi';

const Title = 'Log Fill Activity';

export const SHIPPED_ACTIVITY = 'Pharmacy scheduled delivery to member';

const ActivityOptions = [
    'FMH onboarding outreach attempt 1',
    'FMH onboarding outreach attempt 2',
    'Referral support direct pharmacy outreach',
    'Pharmacy non-responsive via bi-directional messaging',
    'FMH onboarding outreach to SP attempts exhausted',
    'Escalated to PSS for direct pharmacy outreach',
    'PSS outreach',
    'Escalated to client - prescriber information needed',
    'FMH escalated to Plan - patient information needed',
    'Member preference at alternative SP',
    'Escalated to client - member preference',
    'Referral in process',
    'Waiting for Rx',
    'Waiting on callback from prescriber',
    'Prescriber outreach attempts exhausted',
    'Provided SP with pharmacy transfer information',
    'Escalated to NDP - SP unable to get Rx',
    'URGENT - FMH escalated to FMH Pharmacy - Rx needed',
    'NDP initiated Rx transfer from SP attempt 1',
    'NDP initiated Rx transfer from SP attempt 2',
    'NDP outreach to MDO attempt 1',
    'NDP outreach to MDO attempt 2',
    'NDP outreach to MDO attempt 3',
    'NDP - Rx transferred to SP',
    'NDP - exhausted all attempts to get Rx',
    'Escalated to client - unable to obtain Rx',
    'Suspend - unable to reach prescriber',
    'Referred to manufacturer hub',
    'Rx in hand',
    'Rx in hand - additional information needed from MDO',
    'Confirming patient eligibility',
    'Payer verification needed',
    'PA issue - auth needed',
    'PA issue - non-formulary',
    'PA issue - prior auth in process',
    'PA issue - prior auth appeal pending',
    'PA issue - prior auth appeal denied',
    'Escalated to client - PA issue',
    'PA issue - resolved',
    'Adjudication issue - refill too soon',
    'Adjudication issue - plan limitation exceeded',
    'Adjudication issue - max day supply',
    'Adjudication issue - drug utilization review',
    'Adjudication issue - lock issue',
    'Escalated to client - adjudication issue',
    'Adjudication issue - resolved',
    'Claims issue - rebilling',
    'Waiting for financial assistance information',
    'Financial assistance information obtained',
    'Waiting on member call back',
    'Member outreach attempts exhausted',
    'Escalated to client - unable to reach member',
    'Suspend - unable to reach member',
    'Waiting on nursing services',
    'Ready for shipment',
    SHIPPED_ACTIVITY,
    'Pharmacy filled Rx - claim not received',
    'Reported future NBD - refill too soon',
    'Reported future NBD - member has drug on hand',
    'Therapy delay unspecified',
    'Rx on hold with SP - member hospitalized',
    'Rx on hold with SP - MD request',
    'Rx on hold with SP - patient request',
    'Rx on hold with SP - other',
    'Rx on hold - member needs appointment',
    'Rx on hold - clinical issue',
    'Cancelled - member coverage termed',
    'Suspend - coverage termed',
    'Cancelled - buy and bill',
    'Cancelled - clinical drug study',
    'Cancelled - clinical issue',
    'Cancelled - death',
    'Cancelled - denied reauthorization',
    'Cancelled - discontinued therapy',
    'Cancelled - hospice',
    'Cancelled - hospitalized',
    'Cancelled - insurance/financial assistance',
    'Cancelled - patient request',
    'Cancelled - MD request',
    'Cancelled - OON',
    'Cancelled - pharmacy unable to service member state',
    'Cancelled - other',
    'Cancelled - therapy complete',
    'Cancelled - therapy change',
    'Cancelled - noncompliance',
    'Cancelled - switched therapy',
    'Cancelled - nonresponse to therapy',
    'Cancelled - unable to tolerate',
    'Out of stock',
    'Drug shortage',
    'Referral filling under alternate formulation',
    'Referral filling under alternate member ID',
    'Rx transferred to FMH Pharmacy',
    'Pharmacist Rx transfer needed',
];

const CarrierOptions = ['DHL', 'FedEx', 'UPS', 'USPS'];

export const LogFillActivityDialog = ({
    isOpen = false,
    opportunityDetails = undefined,
    referralDetails = undefined,
    onClose,
}) => {
    const referralId = referralDetails?.referralId ?? opportunityDetails?.referralId;
    const [postOnboardingActivity] = usePostOnboardingActivityMutation();
    const [postShippingInformation] = usePostShippingInfoMutation();

    return (
        <Formik
            initialValues={SingleSelectWithOther.initialValues({ id: 'status' })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await postOnboardingActivity({
                        referralId,
                        body: SingleSelectWithOther.submitValues({ id: 'status' })(values),
                    }).unwrap();
                    if (values.status === SHIPPED_ACTIVITY && values.tracking_number) {
                        await postShippingInformation({
                            referralId,
                            payerId: referralDetails?.payerId,
                            body: {
                                tracking_number: values.tracking_number,
                                shipment_date: values.shipment_date ?? null,
                                ...(values.shipment_carrier
                                    ? SingleSelectWithOther.submitValues({
                                          id: 'shipment_carrier',
                                      })(values)
                                    : {
                                          shipment_carrier: null,
                                      }),
                            },
                        }).unwrap();
                    }
                    SuccessToast({ message: 'Fill activity documented successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to document fill activity. Please try again.', err);
                }
            }}
            validationSchema={Yup.object({
                ...SingleSelectWithOther.validationSchema({ id: 'status', required: true }),
                tracking_number: Yup.string(),
                shipment_date: DateInputFormik.validator(),
                ...SingleSelectWithOther.validationSchema({
                    id: 'shipment_carrier',
                    required: false,
                }),
            })}
            enableReinitialize
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle={Title}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Log Fill Activity"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <>
                            <SingleSelectWithOther
                                id="status"
                                label="Fill Activity"
                                required
                                values={ActivityOptions}
                            />
                            {formik.values.status === SHIPPED_ACTIVITY && (
                                <>
                                    <TextInputFormik
                                        id="tracking_number"
                                        label="Last Tracking Number"
                                    />
                                    {formik.values.tracking_number && (
                                        <>
                                            <DateInputFormik
                                                id="shipment_date"
                                                label="Shipment Date"
                                            />
                                            <SingleSelectWithOther
                                                id="shipment_carrier"
                                                label="Shipment Carrier"
                                                values={CarrierOptions}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

LogFillActivityDialog.Title = Title;

LogFillActivityDialog.propTypes = {
    isOpen: PropTypes.bool,
    opportunityDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
    }),
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
    }),
    onClose: PropTypes.func.isRequired,
};
